import React, { useEffect, useRef } from "react";
import * as THREE from "three";

// Define a mobile breakpoint (adjust as needed)
const MOBILE_BREAKPOINT = 768;

const FogComponent = () => {
	const canvasRef = useRef(null);

	// Debounce function (remains the same)
	const debounce = (callback, duration) => {
		let timer;
		return function (event) {
			clearTimeout(timer);
			timer = setTimeout(() => callback(event), duration);
		};
	};

	// Texture loader (remains the same)
	const loadTexs = (imgs, callback) => {
		const texLoader = new THREE.TextureLoader();
		const length = Object.keys(imgs).length;
		const loadedTexs = {};
		let count = 0;

		texLoader.crossOrigin = "anonymous";

		for (let key in imgs) {
			if (imgs.hasOwnProperty(key)) {
				const k = key;
				texLoader.load(imgs[k], (tex) => {
					tex.repeat = THREE.RepeatWrapping;
					loadedTexs[k] = tex;
					count++;
					if (count >= length) callback(loadedTexs);
				});
			}
		}
	};

	// Fog class
	class Fog {
		constructor() {
			this.uniforms = {
				time: { type: "f", value: 0 },
				tex: { type: "t", value: null },
				// *** NEW: Add uniform for opacity control ***
				uOpacityMultiplier: { type: "f", value: 0.36 }, // Default desktop value
			};
			// *** MODIFIED: Default number, can be overridden ***
			this.num = 40; // Default desktop number
			this.obj = null;
		}

		createObj(tex) {
			// --- Check if num was overridden before calling createObj ---
			const actualNum = this.num; // Use the potentially modified number

			const geometry = new THREE.InstancedBufferGeometry();
			const baseGeometry = new THREE.PlaneGeometry(1100, 1100, 20, 20);

			geometry.setAttribute("position", baseGeometry.attributes.position);
			geometry.setAttribute("normal", baseGeometry.attributes.normal);
			geometry.setAttribute("uv", baseGeometry.attributes.uv);
			geometry.setIndex(baseGeometry.index);

			const instancePositions = new THREE.InstancedBufferAttribute(
				new Float32Array(actualNum * 3), // Use actualNum
				3,
				1
			);
			const delays = new THREE.InstancedBufferAttribute(
				new Float32Array(actualNum), // Use actualNum
				1,
				1
			);
			const rotates = new THREE.InstancedBufferAttribute(
				new Float32Array(actualNum), // Use actualNum
				1,
				1
			);

			// Loop based on actualNum
			for (let i = 0; i < actualNum; i++) {
				instancePositions.setXYZ(
					i,
					(Math.random() * 2 - 1) * 850,
					0,
					(Math.random() * 2 - 1) * 300
				);
				delays.setXYZ(i, Math.random());
				rotates.setXYZ(i, Math.random() * 2 + 1);
			}

			geometry.setAttribute("instancePosition", instancePositions);
			geometry.setAttribute("delay", delays);
			geometry.setAttribute("rotate", rotates);

			const material = new THREE.RawShaderMaterial({
				uniforms: this.uniforms,
				// Vertex Shader remains the same
				vertexShader: `
                    attribute vec3 position;
                    attribute vec2 uv;
                    attribute vec3 instancePosition;
                    attribute float delay;
                    attribute float rotate;

                    uniform mat4 projectionMatrix;
                    uniform mat4 modelViewMatrix;
                    uniform float time;

                    varying vec3 vPosition;
                    varying vec2 vUv;
                    varying vec3 vColor;
                    varying float vBlink;

                    const float duration = 200.0;

                    mat4 calcRotateMat4Z(float radian) {
                    return mat4(
                        cos(radian), -sin(radian), 0.0, 0.0,
                        sin(radian), cos(radian), 0.0, 0.0,
                        0.0, 0.0, 1.0, 0.0,
                        0.0, 0.0, 0.0, 1.0
                    );
                    }
                    vec3 convertHsvToRgb(vec3 c) {
                    vec4 K = vec4(1.0, 2.0 / 3.0, 1.0 / 3.0, 3.0);
                    vec3 p = abs(fract(c.xxx + K.xyz) * 6.0 - K.www);
                    return c.z * mix(K.xxx, clamp(p - K.xxx, 0.0, 1.0), c.y);
                    }

                    void main(void) {
                    float now = mod(time + delay * duration, duration) / duration;

                    mat4 rotateMat = calcRotateMat4Z(radians(rotate * 360.0) + time * 0.1);
                    vec3 rotatePosition = (rotateMat * vec4(position, 1.0)).xyz;

                    vec3 moveRise = vec3(
                        (now * 2.0 - 1.0) * (2500.0 - (delay * 2.0 - 1.0) * 2000.0),
                        (now * 2.0 - 1.0) * 2000.0,
                        sin(radians(time * 50.0 + delay + length(position))) * 30.0
                        );
                    vec3 updatePosition = instancePosition + moveRise + rotatePosition;

                    vec3 hsv = vec3(0.37, 0.3, 0.9);
                    vec3 rgb = convertHsvToRgb(hsv);
                    float blink = (sin(radians(now * 360.0 * 20.0)) + 1.0) * 0.88;

                    vec4 mvPosition = modelViewMatrix * vec4(updatePosition, 1.0);

                    vPosition = position;
                    vUv = uv;
                    vColor = rgb;
                    vBlink = blink;

                    gl_Position = projectionMatrix * mvPosition;
                    }
                `,
				// *** MODIFIED: Fragment Shader uses the new uniform ***
				fragmentShader: `
                    precision highp float;

                    uniform sampler2D tex;
                    uniform float uOpacityMultiplier; // Declare the new uniform

                    varying vec3 vPosition;
                    varying vec2 vUv;
                    varying vec3 vColor;
                    varying float vBlink;

                    void main() {
                        vec2 p = vUv * 2.0 - 1.0;

                        vec4 texColor = texture2D(tex, vUv);
                        vec3 color = (texColor.rgb - vBlink * length(p) * 0.8) * vColor;
                        // Use the uniform to modulate opacity
                        float opacity = texColor.a * uOpacityMultiplier;

                        gl_FragColor = vec4(color, opacity);
                    }
                `,
				transparent: true,
				depthWrite: false,
				blending: THREE.AdditiveBlending,
			});
			this.uniforms.tex.value = tex;
			this.obj = new THREE.Mesh(geometry, material);
		}

		render(time) {
			// Only update time if obj exists (prevent errors if not created yet)
			if (this.obj) {
				this.uniforms.time.value += time;
			}
		}
	}

	useEffect(() => {
		const resolution = new THREE.Vector2();
		const canvas = canvasRef.current;
		const renderer = new THREE.WebGLRenderer({
			alpha: true,
			antialias: true,
			canvas: canvas,
		});
		const scene = new THREE.Scene();
		const camera = new THREE.PerspectiveCamera();
		const clock = new THREE.Clock();
		const fog = new Fog(); // Create instance

		camera.far = 50000;
		camera.setFocalLength(24);

		const texsSrc = {
			fog: "https://ykob.github.io/sketch-threejs/img/sketch/fog/fog.png",
		};

		const render = () => {
			// Check if fog object is ready before rendering
			if (fog.obj) {
				const time = clock.getDelta();
				fog.render(time);
				renderer.render(scene, camera);
			}
		};

		const renderLoop = () => {
			render();
			requestAnimationFrame(renderLoop);
		};

		const resizeCamera = () => {
			camera.aspect = resolution.x / resolution.y;
			camera.updateProjectionMatrix();
		};

		const resizeWindow = () => {
			if (!canvas) return; // Ensure canvas exists
			// Use clientWidth for reliable width measurement
			const width = canvas.clientWidth;
			const height = canvas.clientHeight;

			// *** NOTE: Mobile check is done only on init in this example. ***
			// If you need density to change dynamically when resizing *across* the breakpoint,
			// you'd need more complex logic here, potentially re-creating the fog object.

			resolution.set(width, height);
			renderer.setSize(resolution.x, resolution.y);
			resizeCamera(); // Resize camera after setting renderer size
		};

		const on = () => {
			window.addEventListener("resize", debounce(resizeWindow, 50)); // Shorter debounce
		};
		const off = () => {
			window.removeEventListener("resize", debounce(resizeWindow, 50));
			// It's better practice to remove the *exact* debounced function instance
			// For simplicity here, we remove any listener for 'resize', but a more robust
			// solution would store the debounced function returned by debounce() and remove that specific one.
		};

		const init = () => {
			if (!canvasRef.current) return; // Guard against null ref

			// *** NEW: Check for mobile and adjust fog parameters ***
			const isMobile = window.innerWidth < MOBILE_BREAKPOINT;

			if (isMobile) {
				fog.num = 15; // Reduce number of planes (e.g., from 40 to 20)
				fog.uniforms.uOpacityMultiplier.value = 0.20; // e.g., less than half of desktop
			} else {
				fog.num = 40;
				fog.uniforms.uOpacityMultiplier.value = 0.36;
			}
			// --- End of mobile check ---

			loadTexs(texsSrc, (loadedTexs) => {
				// Now create the object with the potentially adjusted parameters
				fog.createObj(loadedTexs.fog);
				scene.add(fog.obj);

				renderer.setClearColor(0x000000, 0); // Use 0x000000 for black with alpha 0
				camera.position.set(0, 0, 1000);
				camera.lookAt(new THREE.Vector3(0, 0, 0)); // Explicitly look at origin
				clock.start();

				on();
				resizeWindow(); // Initial size setup
				renderLoop();
			});
		};

		init();

		// Cleanup
		return () => {
			off(); // Remove listener
			// Dispose Three.js resources
			scene.remove(fog.obj);
			if (fog.obj && fog.obj.geometry) fog.obj.geometry.dispose();
			if (fog.obj && fog.obj.material) fog.obj.material.dispose();
			if (fog.uniforms.tex.value) fog.uniforms.tex.value.dispose();
			renderer.dispose();
			// Cancel animation frame if loop is running
			// (This basic example doesn't store the request ID, but in complex apps you might)
			console.log("FogComponent cleanup");
		};
	}, []); // Empty dependency array ensures this runs once on mount

	return (
		<canvas
			ref={canvasRef}
			id="canvas-webgl"
			className="p-canvas-webgl"
			// Ensure canvas covers the area and interacts with layout correctly
			style={{
				display: "block", // Avoid extra space below canvas
				width: "100%",
				height: "100%",
				position: "absolute",
				top: 0,
				left: 0,
				zIndex: -1, // Optional: Place behind other content if needed
			}}
		/>
	);
};

export default FogComponent;
